import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import Modal from '../../components/ui/Modal';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import axiosInstance from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import {
  ArrowLeft, Edit2, ChevronLeft, ChevronRight, Plus, ExternalLink, Copy,
  Lock, Phone, Mail, Link as LinkIcon, DollarSign,
  CheckCircle2, X, Ban, Loader2, AlertCircle,
} from 'lucide-react';
import './AffiliateDetail.css';

// WhatsApp brand mark — Lucide doesn't ship one.
const WhatsAppIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"/>
  </svg>
);

const COMMISSION_FILTERS = [
  { value: '',          label: 'All' },
  { value: 'pending',   label: 'Pending' },
  { value: 'approved',  label: 'Approved' },
  { value: 'paid',      label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_PILL = {
  active:      'affd-pill-success',
  paused:      'affd-pill-warn',
  deactivated: 'affd-pill-muted',
  pending:     'affd-pill-warn',
  approved:    'affd-pill-info',
  paid:        'affd-pill-success',
  cancelled:   'affd-pill-danger',
};

function formatRevenue(value, currency = '₦') {
  if (!value || value === 0) return `${currency}0`;
  if (value >= 1_000_000) return `${currency}${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000)     return `${currency}${(value / 1_000).toFixed(1)}K`;
  return `${currency}${Math.round(value).toLocaleString()}`;
}

function formatNumber(n) {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

const statusLabel = (s) => {
  const v = (s || 'active').toLowerCase();
  return v.charAt(0).toUpperCase() + v.slice(1);
};

export default function AffiliateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  // Deep Links is a separate feature (marketing.deep_links). A merchant
  // can have affiliates on but deep-links off (e.g. Pro tier where
  // deep-links is Premium+). When that's the case we still SHOW any
  // deep links already on the affiliate (continuity), but disable the
  // Assign/Generate actions and surface an inline upgrade prompt.
  const { hasAccess: hasDeepLinksAccess, loading: deepLinksAccessLoading } =
    useFeatureAccess('marketing.deep_links');

  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState(null);

  const [commissions, setCommissions] = useState([]);
  const [commissionPagination, setCommissionPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [commissionFilter, setCommissionFilter] = useState('');
  const [selectedCommissions, setSelectedCommissions] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editForm, setEditForm] = useState({
    name: '', email: '', phone: '',
    instagramHandle: '', tiktokHandle: '',
    commissionType: 'percentage', commissionRate: '', commissionCap: '',
    notes: '', status: 'active',
  });

  const [linkForm, setLinkForm] = useState({
    destinationUrl: '', title: '', campaign: '', tags: '',
  });

  const [payForm, setPayForm] = useState({ paymentReference: '', paymentNote: '' });

  const [showInactiveLinks, setShowInactiveLinks] = useState(false);
  const [unassignedLinks, setUnassignedLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);

  const fetchAffiliate = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/affiliates/${id}`);
      if (res.data.success) setAffiliate(res.data.affiliate);
    } catch (err) {
      console.error('Failed to fetch affiliate:', err);
      if (err.response?.status === 404) {
        showToast('Affiliate not found', 'error');
        navigate('/affiliates');
      }
    }
  }, [id, navigate, showToast]);

  const fetchCommissions = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: commissionPagination.page,
        limit: commissionPagination.limit,
      });
      if (commissionFilter) params.set('status', commissionFilter);
      const res = await axiosInstance.get(`/affiliates/${id}/commissions?${params}`);
      if (res.data.success) {
        setCommissions(res.data.commissions);
        setCommissionPagination(res.data.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch commissions:', err);
    }
  }, [id, commissionPagination.page, commissionPagination.limit, commissionFilter]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchAffiliate(), fetchCommissions()]);
      setLoading(false);
    };
    load();
  }, [fetchAffiliate, fetchCommissions]);

  /* ─── Handlers ──────────────────────────────────────────────── */
  const openEditModal = () => {
    if (!affiliate) return;
    setEditForm({
      name: affiliate.name || '',
      email: affiliate.email || '',
      phone: affiliate.phone || '',
      instagramHandle: affiliate.instagramHandle || '',
      tiktokHandle: affiliate.tiktokHandle || '',
      commissionType: (affiliate.commissionType || 'percentage').toLowerCase(),
      commissionRate: affiliate.commissionRate?.toString() || '',
      commissionCap: affiliate.commissionCap?.toString() || '',
      notes: affiliate.notes || '',
      status: (affiliate.status || 'active').toLowerCase(),
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim())  { showToast('Name is required', 'error'); return; }
    if (!editForm.email.trim()) { showToast('Email is required', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        commissionRate: parseFloat(editForm.commissionRate),
        commissionCap: editForm.commissionCap ? parseFloat(editForm.commissionCap) : null,
      };
      const res = await axiosInstance.put(`/affiliates/${id}`, payload);
      if (res.data.success) {
        showToast('Affiliate updated', 'success');
        setShowEditModal(false);
        fetchAffiliate();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update affiliate', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateLink = async () => {
    if (!linkForm.destinationUrl.trim()) { showToast('Destination URL is required', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        destinationUrl: linkForm.destinationUrl,
        title: linkForm.title || undefined,
        campaign: linkForm.campaign || undefined,
        tags: linkForm.tags ? linkForm.tags.split(',').map((t) => t.trim()).filter(Boolean) : undefined,
      };
      const res = await axiosInstance.post(`/affiliates/${id}/links`, payload);
      if (res.data.success) {
        showToast('Deep link generated', 'success');
        setShowLinkModal(false);
        setLinkForm({ destinationUrl: '', title: '', campaign: '', tags: '' });
        fetchAffiliate();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to generate link', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveCommissions = async () => {
    if (selectedCommissions.length === 0) { showToast('No commissions selected', 'error'); return; }
    setSaving(true);
    try {
      const res = await axiosInstance.post('/affiliates/commissions/approve', {
        commissionIds: selectedCommissions,
      });
      if (res.data.success) {
        showToast(`${res.data.approvedCount} commission${res.data.approvedCount === 1 ? '' : 's'} approved`, 'success');
        setSelectedCommissions([]);
        fetchCommissions();
        fetchAffiliate();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to approve commissions', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelCommissions = async () => {
    if (selectedCommissions.length === 0) { showToast('No commissions selected', 'error'); return; }
    const ok = await confirm(
      `Cancel ${selectedCommissions.length} commission${selectedCommissions.length === 1 ? '' : 's'}? This reverses the affiliate counters.`,
      { title: 'Cancel commissions?', confirmText: 'Cancel commissions', cancelText: 'Keep', type: 'warning' }
    );
    if (!ok) return;
    setSaving(true);
    try {
      const res = await axiosInstance.post('/affiliates/commissions/cancel', {
        commissionIds: selectedCommissions,
        reason: 'Cancelled by merchant',
      });
      if (res.data.success) {
        showToast(`${res.data.cancelledCount} commission${res.data.cancelledCount === 1 ? '' : 's'} cancelled`, 'success');
        setSelectedCommissions([]);
        fetchCommissions();
        fetchAffiliate();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to cancel commissions', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!payForm.paymentReference.trim()) { showToast('Payment reference is required', 'error'); return; }
    if (selectedCommissions.length === 0)  { showToast('No commissions selected', 'error'); return; }
    setSaving(true);
    try {
      const res = await axiosInstance.post('/affiliates/commissions/mark-paid', {
        commissionIds: selectedCommissions,
        paymentReference: payForm.paymentReference,
        paymentNote: payForm.paymentNote || undefined,
      });
      if (res.data.success) {
        showToast(`${selectedCommissions.length} commission${selectedCommissions.length === 1 ? '' : 's'} marked as paid`, 'success');
        setShowPayModal(false);
        setPayForm({ paymentReference: '', paymentNote: '' });
        setSelectedCommissions([]);
        fetchCommissions();
        fetchAffiliate();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to mark as paid', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getPortalUrl = () => affiliate?.storefrontBaseUrl
    ? `${affiliate.storefrontBaseUrl}/affiliate/${affiliate.referralCode}`
    : null;

  const copyPortalLink = () => {
    const url = getPortalUrl();
    if (!url) { showToast('Portal URL not available', 'error'); return; }
    navigator.clipboard.writeText(url)
      .then(() => showToast('Portal link copied', 'success'))
      .catch(() => showToast('Failed to copy link', 'error'));
  };

  const openAssignModal = async () => {
    setLoadingLinks(true);
    setShowAssignModal(true);
    try {
      const res = await axiosInstance.get('/deep-links?limit=100');
      if (res.data.success) {
        setUnassignedLinks((res.data.links || []).filter((l) => !l.affiliateId));
      }
    } catch (err) {
      showToast('Failed to load deep links', 'error');
    } finally {
      setLoadingLinks(false);
    }
  };

  const handleAssignLink = async (linkId) => {
    setSaving(true);
    try {
      const res = await axiosInstance.put(`/deep-links/${linkId}/assign-affiliate`, { affiliateId: id });
      if (res.data.success) {
        showToast('Link assigned to affiliate', 'success');
        setShowAssignModal(false);
        fetchAffiliate();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to assign link', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleCommission = (commissionId) => {
    setSelectedCommissions((prev) =>
      prev.includes(commissionId) ? prev.filter((cid) => cid !== commissionId) : [...prev, commissionId]
    );
  };

  const toggleAllCommissions = () => {
    const payable = commissions.filter((c) => c.status === 'PENDING' || c.status === 'APPROVED');
    setSelectedCommissions(
      selectedCommissions.length === payable.length && payable.length > 0
        ? []
        : payable.map((c) => c.id)
    );
  };

  /* ─── Render ────────────────────────────────────────────────── */
  if (loading) {
    return (
      <Shell>
        <FeatureGate featureId="marketing.affiliates" showUpgradePrompt>
          <div className="affd-page">
            <div className="affd-loading"><Loader2 size={20} className="affd-spin" /> Loading affiliate…</div>
          </div>
        </FeatureGate>
      </Shell>
    );
  }

  if (!affiliate) {
    return (
      <Shell>
        <FeatureGate featureId="marketing.affiliates" showUpgradePrompt>
          <div className="affd-page">
            <div className="affd-empty">
              <div className="affd-empty-icon"><AlertCircle size={22} /></div>
              <h2 className="affd-empty-title">Affiliate not found</h2>
              <p className="affd-empty-desc">It may have been removed.</p>
              <Link to="/affiliates" className="affd-btn affd-btn-secondary">
                <ArrowLeft size={14} /> Back to affiliates
              </Link>
            </div>
          </div>
        </FeatureGate>
      </Shell>
    );
  }

  const allDeepLinks = affiliate.deepLinks || [];
  const deepLinks = showInactiveLinks ? allDeepLinks : allDeepLinks.filter((l) => l.isActive !== false);
  const inactiveCount = allDeepLinks.filter(
    (l) => l.isActive === false || (l.expiresAt && new Date(l.expiresAt) < new Date())
  ).length;

  const status = (affiliate.status || 'active').toLowerCase();
  const portalUrl = getPortalUrl();

  // Compact phone for tel: + wa.me links
  const cleanPhone = affiliate.phone ? affiliate.phone.replace(/[^0-9]/g, '') : '';

  return (
    <Shell>
      <FeatureGate featureId="marketing.affiliates" showUpgradePrompt>
        <div className="affd-page">

          {/* ── Breadcrumb ──────────────────────────────────────── */}
          <Link to="/affiliates" className="affd-back">
            <ArrowLeft size={14} /> Back to affiliates
          </Link>

          {/* ── Header ──────────────────────────────────────────── */}
          <header className="affd-header">
            <div className="affd-header-text">
              <h1 className="affd-title">
                {affiliate.name}
                <span className={`affd-pill ${STATUS_PILL[status] || 'affd-pill-muted'}`}>
                  {statusLabel(status)}
                </span>
              </h1>
              <p className="affd-subtitle">{affiliate.email}</p>
            </div>
            <div className="affd-header-actions">
              {affiliate.phone && (
                <a
                  href={`tel:${affiliate.phone}`}
                  className="affd-icon-btn"
                  title={`Call ${affiliate.phone}`}
                  aria-label="Call"
                >
                  <Phone size={14} />
                </a>
              )}
              {cleanPhone && (
                <a
                  href={`https://wa.me/${cleanPhone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="affd-icon-btn affd-icon-btn-wa"
                  title="WhatsApp"
                  aria-label="WhatsApp"
                >
                  <WhatsAppIcon size={15} />
                </a>
              )}
              {affiliate.email && (
                <a
                  href={`mailto:${affiliate.email}`}
                  className="affd-icon-btn"
                  title={`Email ${affiliate.email}`}
                  aria-label="Email"
                >
                  <Mail size={14} />
                </a>
              )}
              <button type="button" className="affd-btn affd-btn-secondary" onClick={openEditModal}>
                <Edit2 size={14} /> <span className="affd-btn-text">Edit profile</span>
              </button>
            </div>
          </header>

          {/* ── Stat tiles (3-up) ───────────────────────────────── */}
          <section className="affd-stat-grid">
            <div className="affd-stat">
              <span className="affd-stat-label">Clicks · conversions</span>
              <span className="affd-stat-value">
                {formatNumber(affiliate.totalClicks || 0)}
                <span className="affd-stat-sub">/ {affiliate.totalConversions || 0} conv</span>
              </span>
            </div>
            <div className="affd-stat">
              <span className="affd-stat-label">Revenue generated</span>
              <span className="affd-stat-value">{formatRevenue(affiliate.totalRevenue)}</span>
            </div>
            <div className="affd-stat">
              <span className="affd-stat-label">Commission</span>
              <span className="affd-stat-value affd-stat-value-success">{formatRevenue(affiliate.totalCommission)}</span>
              <span className="affd-stat-sub">
                <span className="affd-stat-sub-warn">{formatRevenue(affiliate.pendingCommission)} pending</span>
                {' · '}
                {formatRevenue(affiliate.paidCommission)} paid
              </span>
            </div>
          </section>

          {/* ── Profile card ────────────────────────────────────── */}
          <section className="affd-card">
            <div className="affd-card-head">
              <h2 className="affd-card-title">Profile</h2>
            </div>
            <dl className="affd-kv">
              <div className="affd-kv-row">
                <dt>Phone</dt>
                <dd className={affiliate.phone ? '' : 'is-muted'}>{affiliate.phone || 'Not provided'}</dd>
              </div>
              <div className="affd-kv-row">
                <dt>Instagram</dt>
                <dd className={affiliate.instagramHandle ? '' : 'is-muted'}>
                  {affiliate.instagramHandle ? `@${affiliate.instagramHandle}` : 'Not provided'}
                </dd>
              </div>
              <div className="affd-kv-row">
                <dt>TikTok</dt>
                <dd className={affiliate.tiktokHandle ? '' : 'is-muted'}>
                  {affiliate.tiktokHandle ? `@${affiliate.tiktokHandle}` : 'Not provided'}
                </dd>
              </div>
              <div className="affd-kv-row">
                <dt>Commission</dt>
                <dd>
                  {affiliate.commissionType?.toUpperCase() === 'PERCENTAGE'
                    ? `${affiliate.commissionRate}%`
                    : `${formatRevenue(affiliate.commissionRate)} per sale`}
                  {affiliate.commissionCap ? ` (cap ${formatRevenue(affiliate.commissionCap)})` : ''}
                </dd>
              </div>
              {affiliate.bankDetails && (
                <>
                  <div className="affd-kv-row">
                    <dt>Bank</dt>
                    <dd className={affiliate.bankDetails.bankName ? '' : 'is-muted'}>
                      {affiliate.bankDetails.bankName || 'Not provided'}
                    </dd>
                  </div>
                  <div className="affd-kv-row">
                    <dt>Account number</dt>
                    <dd className={`affd-kv-mono ${affiliate.bankDetails.accountNumber ? '' : 'is-muted'}`}>
                      {affiliate.bankDetails.accountNumber || 'Not provided'}
                    </dd>
                  </div>
                  <div className="affd-kv-row">
                    <dt>Account name</dt>
                    <dd className={affiliate.bankDetails.accountName ? '' : 'is-muted'}>
                      {affiliate.bankDetails.accountName || 'Not provided'}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </section>

          {/* ── Portal card ─────────────────────────────────────── */}
          {portalUrl && (
            <section className="affd-card">
              <div className="affd-card-head">
                <h2 className="affd-card-title">Affiliate portal</h2>
                <button type="button" className="affd-btn affd-btn-ghost" onClick={copyPortalLink}>
                  <Copy size={13} /> Copy link
                </button>
              </div>
              <div className="affd-portal">
                <a
                  href={portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="affd-portal-url"
                >
                  {portalUrl}
                  <ExternalLink size={12} />
                </a>
                <div className="affd-portal-code">
                  <span className="affd-portal-code-label">Referral code</span>
                  <code>{affiliate.referralCode}</code>
                </div>
              </div>
            </section>
          )}

          {/* ── Deep Links section ──────────────────────────────── */}
          <section className="affd-card">
            <div className="affd-card-head">
              <h2 className="affd-card-title">Deep links <span className="affd-card-count">({deepLinks.length})</span></h2>
              <div className="affd-card-actions">
                {inactiveCount > 0 && (
                  <button
                    type="button"
                    className={`affd-btn affd-btn-ghost${showInactiveLinks ? ' is-active' : ''}`}
                    onClick={() => setShowInactiveLinks(!showInactiveLinks)}
                  >
                    {showInactiveLinks ? 'Hide inactive' : `Show inactive (${inactiveCount})`}
                  </button>
                )}
                {!deepLinksAccessLoading && hasDeepLinksAccess && (
                  <>
                    <button type="button" className="affd-btn affd-btn-secondary" onClick={openAssignModal}>
                      <LinkIcon size={13} /> <span className="affd-btn-text">Assign existing</span>
                    </button>
                    <button
                      type="button"
                      className="affd-btn affd-btn-primary"
                      onClick={() => { setLinkForm({ destinationUrl: '', title: '', campaign: '', tags: '' }); setShowLinkModal(true); }}
                    >
                      <Plus size={13} /> <span className="affd-btn-text">Generate link</span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Locked banner — Affiliates on but Deep Links off */}
            {!deepLinksAccessLoading && !hasDeepLinksAccess && (
              <div className="affd-locked-banner">
                <Lock size={16} />
                <div className="affd-locked-text">
                  <strong>Deep Links is a Premium feature.</strong>
                  <span>
                    Existing links remain active and continue earning commissions.
                    To assign new links or generate fresh ones for this affiliate,
                    <Link to="/settings?tab=billing"> upgrade your plan</Link>.
                  </span>
                </div>
              </div>
            )}

            {deepLinks.length === 0 ? (
              <div className="affd-empty-inline">No deep links assigned to this affiliate yet.</div>
            ) : (
              <ul className="affd-list">
                {deepLinks.map((link) => {
                  const expired = link.expiresAt && new Date(link.expiresAt) < new Date();
                  const linkStatus = !link.isActive ? 'inactive' : expired ? 'expired' : 'active';
                  const pillClass =
                    linkStatus === 'active' ? 'affd-pill-success'
                    : linkStatus === 'expired' ? 'affd-pill-warn'
                    : 'affd-pill-muted';
                  return (
                    <li key={link.id} className={`affd-row${linkStatus !== 'active' ? ' is-dimmed' : ''}`}>
                      <div className="affd-link-row">
                        <span className="affd-link-title">{link.title || link.destinationUrl}</span>
                        <span className="affd-link-short">{link.shortUrl || link.shortCode}</span>
                        <span className={`affd-pill ${pillClass}`}>
                          {linkStatus === 'active' ? 'Active' : linkStatus === 'expired' ? 'Expired' : 'Inactive'}
                        </span>
                        <span className="affd-link-stats">
                          <strong>{formatNumber(link.totalClicks || 0)}</strong> clicks ·
                          {' '}{link.totalConversions || 0} conv ·
                          {' '}{formatRevenue(link.totalRevenue)}
                        </span>
                        <span className="affd-link-date">{new Date(link.createdAt).toLocaleDateString()}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* ── Commissions section ─────────────────────────────── */}
          <section className="affd-card">
            <div className="affd-card-head">
              <h2 className="affd-card-title">
                Commissions <span className="affd-card-count">({commissionPagination.total || 0})</span>
              </h2>
              <select
                className="affd-input affd-select affd-card-select"
                value={commissionFilter}
                onChange={(e) => {
                  setCommissionFilter(e.target.value);
                  setCommissionPagination((p) => ({ ...p, page: 1 }));
                }}
              >
                {COMMISSION_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            {selectedCommissions.length > 0 && (
              <div className="affd-bulk">
                <span className="affd-bulk-count">{selectedCommissions.length} selected</span>
                <div className="affd-bulk-actions">
                  <button
                    type="button"
                    className="affd-btn affd-btn-secondary"
                    onClick={handleApproveCommissions}
                    disabled={saving}
                  >
                    <CheckCircle2 size={13} /> Approve
                  </button>
                  <button
                    type="button"
                    className="affd-btn affd-btn-primary"
                    onClick={() => { setPayForm({ paymentReference: '', paymentNote: '' }); setShowPayModal(true); }}
                    disabled={saving}
                  >
                    <DollarSign size={13} /> Mark paid
                  </button>
                  <button
                    type="button"
                    className="affd-btn affd-btn-danger"
                    onClick={handleCancelCommissions}
                    disabled={saving}
                  >
                    <Ban size={13} /> Cancel
                  </button>
                  <button
                    type="button"
                    className="affd-btn affd-btn-ghost"
                    onClick={() => setSelectedCommissions([])}
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {commissions.length === 0 ? (
              <div className="affd-empty-inline">No commissions recorded yet.</div>
            ) : (
              <>
                <ul className="affd-list">
                  {commissions.map((c) => {
                    const isPayable = c.status === 'PENDING' || c.status === 'APPROVED';
                    const cStatus = (c.status || '').toLowerCase();
                    const isPercent = affiliate?.commissionType?.toUpperCase() === 'PERCENTAGE';
                    return (
                      <li key={c.id} className="affd-row">
                        <div className="affd-comm-row">
                          <label className="affd-comm-check">
                            <input
                              type="checkbox"
                              checked={selectedCommissions.includes(c.id)}
                              onChange={() => toggleCommission(c.id)}
                              disabled={!isPayable}
                              aria-label="Select commission"
                            />
                          </label>
                          <span className="affd-comm-order">{formatRevenue(c.orderValue)}</span>
                          <span className="affd-comm-rate">@ {c.commissionRate}{isPercent ? '%' : ''}</span>
                          <span className="affd-comm-amount">{formatRevenue(c.commissionAmount)}</span>
                          <span className={`affd-pill ${STATUS_PILL[cStatus] || 'affd-pill-muted'}`}>
                            {statusLabel(cStatus)}
                          </span>
                          <span className="affd-comm-date">
                            {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {commissionPagination.pages > 1 && (
                  <nav className="affd-pagination" aria-label="Pagination">
                    <button
                      type="button"
                      className="affd-page-btn"
                      disabled={commissionPagination.page <= 1}
                      onClick={() => setCommissionPagination((p) => ({ ...p, page: p.page - 1 }))}
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <span className="affd-page-info">
                      Page <strong>{commissionPagination.page}</strong> of {commissionPagination.pages}
                    </span>
                    <button
                      type="button"
                      className="affd-page-btn"
                      disabled={commissionPagination.page >= commissionPagination.pages}
                      onClick={() => setCommissionPagination((p) => ({ ...p, page: p.page + 1 }))}
                      aria-label="Next page"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </nav>
                )}

                {commissions.length > 0 && (
                  <button
                    type="button"
                    className="affd-select-all-link"
                    onClick={toggleAllCommissions}
                  >
                    {selectedCommissions.length > 0 ? 'Clear selection' : 'Select all payable on this page'}
                  </button>
                )}
              </>
            )}
          </section>

          {/* ─────────── Modals ─────────── */}

          {/* Edit Profile */}
          <Modal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            title="Edit affiliate"
            size="md"
            padded={false}
            footer={
              <>
                <button type="button" className="affd-btn affd-btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button
                  type="submit"
                  form="affd-edit-form"
                  className="affd-btn affd-btn-primary"
                  disabled={saving}
                >
                  {saving ? <Loader2 size={14} className="affd-spin" /> : null}
                  Update profile
                </button>
              </>
            }
          >
            <form
              id="affd-edit-form"
              onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}
              className="affd-form"
            >
              <div className="affd-form-row">
                <div className="affd-field">
                  <label className="affd-field-label">Name</label>
                  <input
                    className="affd-input"
                    placeholder="Full name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="affd-field">
                  <label className="affd-field-label">Email</label>
                  <input
                    type="email"
                    className="affd-input"
                    placeholder="email@example.com"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="affd-field">
                <label className="affd-field-label">Phone</label>
                <input
                  type="tel"
                  className="affd-input"
                  placeholder="+234 800 000 0000"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </div>
              <div className="affd-form-row">
                <div className="affd-field">
                  <label className="affd-field-label">Instagram</label>
                  <input
                    className="affd-input"
                    placeholder="@username"
                    value={editForm.instagramHandle}
                    onChange={(e) => setEditForm({ ...editForm, instagramHandle: e.target.value })}
                  />
                </div>
                <div className="affd-field">
                  <label className="affd-field-label">TikTok</label>
                  <input
                    className="affd-input"
                    placeholder="@username"
                    value={editForm.tiktokHandle}
                    onChange={(e) => setEditForm({ ...editForm, tiktokHandle: e.target.value })}
                  />
                </div>
              </div>
              <div className="affd-form-row">
                <div className="affd-field">
                  <label className="affd-field-label">Commission type</label>
                  <select
                    className="affd-input affd-select"
                    value={editForm.commissionType}
                    onChange={(e) => setEditForm({ ...editForm, commissionType: e.target.value })}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed amount</option>
                  </select>
                </div>
                <div className="affd-field">
                  <label className="affd-field-label">
                    Rate {editForm.commissionType === 'percentage' ? '(%)' : '(₦)'}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    className="affd-input"
                    placeholder={editForm.commissionType === 'percentage' ? '10' : '500'}
                    value={editForm.commissionRate}
                    onChange={(e) => setEditForm({ ...editForm, commissionRate: e.target.value })}
                  />
                </div>
              </div>
              <div className="affd-form-row">
                <div className="affd-field">
                  <label className="affd-field-label">Cap per order (₦) — optional</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="affd-input"
                    placeholder="No cap"
                    value={editForm.commissionCap}
                    onChange={(e) => setEditForm({ ...editForm, commissionCap: e.target.value })}
                  />
                </div>
                <div className="affd-field">
                  <label className="affd-field-label">Status</label>
                  <select
                    className="affd-input affd-select"
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="deactivated">Deactivated</option>
                  </select>
                </div>
              </div>
              <div className="affd-field">
                <label className="affd-field-label">Notes (internal)</label>
                <textarea
                  className="affd-input affd-textarea"
                  placeholder="Internal notes about this affiliate…"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                />
              </div>
            </form>
          </Modal>

          {/* Generate Link */}
          <Modal
            isOpen={showLinkModal}
            onClose={() => setShowLinkModal(false)}
            title="Generate affiliate link"
            size="md"
            padded={false}
            footer={
              <>
                <button type="button" className="affd-btn affd-btn-secondary" onClick={() => setShowLinkModal(false)}>Cancel</button>
                <button
                  type="submit"
                  form="affd-link-form"
                  className="affd-btn affd-btn-primary"
                  disabled={saving}
                >
                  {saving ? <Loader2 size={14} className="affd-spin" /> : <LinkIcon size={14} />}
                  Generate link
                </button>
              </>
            }
          >
            <form
              id="affd-link-form"
              onSubmit={(e) => { e.preventDefault(); handleGenerateLink(); }}
              className="affd-form"
            >
              <div className="affd-field">
                <label className="affd-field-label">Destination URL</label>
                <input
                  type="url"
                  className="affd-input"
                  placeholder="https://yourstore.userevstar.com/products/example"
                  value={linkForm.destinationUrl}
                  onChange={(e) => setLinkForm({ ...linkForm, destinationUrl: e.target.value })}
                  required
                />
              </div>
              <div className="affd-form-row">
                <div className="affd-field">
                  <label className="affd-field-label">Title</label>
                  <input
                    className="affd-input"
                    placeholder="e.g. Summer Collection"
                    value={linkForm.title}
                    onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
                  />
                </div>
                <div className="affd-field">
                  <label className="affd-field-label">Campaign</label>
                  <input
                    className="affd-input"
                    placeholder="e.g. Summer 2026"
                    value={linkForm.campaign}
                    onChange={(e) => setLinkForm({ ...linkForm, campaign: e.target.value })}
                  />
                </div>
              </div>
              <div className="affd-field">
                <label className="affd-field-label">Tags</label>
                <input
                  className="affd-input"
                  placeholder="instagram, promo, new-arrivals"
                  value={linkForm.tags}
                  onChange={(e) => setLinkForm({ ...linkForm, tags: e.target.value })}
                />
                <span className="affd-field-hint">Separate multiple tags with commas.</span>
              </div>
            </form>
          </Modal>

          {/* Mark as Paid */}
          <Modal
            isOpen={showPayModal}
            onClose={() => setShowPayModal(false)}
            title="Mark commissions as paid"
            size="md"
            padded={false}
            footer={
              <>
                <button type="button" className="affd-btn affd-btn-secondary" onClick={() => setShowPayModal(false)}>Cancel</button>
                <button
                  type="submit"
                  form="affd-pay-form"
                  className="affd-btn affd-btn-primary"
                  disabled={saving}
                >
                  {saving ? <Loader2 size={14} className="affd-spin" /> : <DollarSign size={14} />}
                  Confirm payment
                </button>
              </>
            }
          >
            <form
              id="affd-pay-form"
              onSubmit={(e) => { e.preventDefault(); handleMarkPaid(); }}
              className="affd-form"
            >
              <p className="affd-form-help">
                Marking <strong>{selectedCommissions.length}</strong> commission{selectedCommissions.length === 1 ? '' : 's'} as paid for <strong>{affiliate.name}</strong>.
              </p>
              <div className="affd-field">
                <label className="affd-field-label">Payment reference</label>
                <input
                  className="affd-input"
                  placeholder="e.g. TRF-20260205-001"
                  value={payForm.paymentReference}
                  onChange={(e) => setPayForm({ ...payForm, paymentReference: e.target.value })}
                  required
                />
                <span className="affd-field-hint">Bank transfer reference or payment ID.</span>
              </div>
              <div className="affd-field">
                <label className="affd-field-label">Note (optional)</label>
                <textarea
                  className="affd-input affd-textarea"
                  placeholder="Anything else worth recording…"
                  value={payForm.paymentNote}
                  onChange={(e) => setPayForm({ ...payForm, paymentNote: e.target.value })}
                />
              </div>
            </form>
          </Modal>

          {/* Assign Existing Link */}
          <Modal
            isOpen={showAssignModal}
            onClose={() => setShowAssignModal(false)}
            title="Assign existing deep link"
            size="md"
            padded={false}
            footer={
              <button type="button" className="affd-btn affd-btn-secondary" onClick={() => setShowAssignModal(false)}>
                Close
              </button>
            }
          >
            <div className="affd-form">
              {loadingLinks ? (
                <div className="affd-loading"><Loader2 size={18} className="affd-spin" /> Loading links…</div>
              ) : unassignedLinks.length === 0 ? (
                <div className="affd-empty-inline">
                  No unassigned deep links available. Create one from the{' '}
                  <Link to="/deep-links" className="affd-inline-link">Deep Links page</Link> first.
                </div>
              ) : (
                <ul className="affd-assign-list">
                  {unassignedLinks.map((link) => (
                    <li key={link.id} className="affd-assign-row">
                      <div className="affd-assign-info">
                        <span className="affd-assign-title">{link.title || 'Untitled'}</span>
                        <span className="affd-assign-url">{link.destinationUrl}</span>
                        <span className="affd-assign-stats">
                          {link.totalClicks || 0} clicks · {link.totalConversions || 0} conversions
                        </span>
                      </div>
                      <button
                        type="button"
                        className="affd-btn affd-btn-primary"
                        onClick={() => handleAssignLink(link.id)}
                        disabled={saving}
                      >
                        Assign
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Modal>

        </div>
      </FeatureGate>
    </Shell>
  );
}
